import { useMutation, useQueryClient } from '@tanstack/react-query';

import { OBJECT_ID_TIMESTAMP_DELIMITER } from 'consts/bookmarks';
import { SEARCH_TYPE } from 'services/search';
import { useApi } from 'services/api';
import type { Page } from 'consts/pages';
import type { IdBookmark, PqlBookmark } from './types';

export type IdRecord = {
  alertId?: number;
  eventTitle?: string;
  indicatorTitle?: string;
  _id?: string;
  ts?: string;
};

type IdFormValues = {
  name?: string;
  description?: string;
  labels?: string;
};

type PqlFormValues = {
  name: string;
  description: string;
  labels?: string;
  query?: string;
  view?: string;
};

type CreateIdBookmarkParams = {
  values: IdFormValues;
  record: IdRecord;
  type: 'id';
};

type CreatePqlBookmarkParams = {
  bookmark: PqlFormValues;
  page: Page;
  query: string;
  activeSensors: number[];
  type: 'pql';
};

export type CreateBookmarkParams = CreateIdBookmarkParams | CreatePqlBookmarkParams;

const getObjectIdAndSearchType = (record: IdRecord) => {
  return (
    (record.alertId && { objectId: record.alertId + '', searchType: 'O_ALERTS' as const }) ||
    (record.eventTitle && { objectId: record.eventTitle, searchType: 'O_EVENTS' as const }) ||
    (record.indicatorTitle && {
      objectId: record.indicatorTitle,
      searchType: 'O_INDICATORS' as const,
    }) ||
    (record._id && {
      objectId: `${record._id}${OBJECT_ID_TIMESTAMP_DELIMITER}${record.ts}`,
      searchType: 'O_SENSOR' as const,
    })
  );
};

const getIdPayload = ({ record, values }: { record: IdRecord; values: IdFormValues }) => {
  const objectIdAndSearchType = getObjectIdAndSearchType(record);

  return {
    name: values.name || record.eventTitle || record.indicatorTitle || record._id,
    description: values.description || '',
    labels: values.labels && typeof values.labels === 'string' ? values.labels.split(',').map((l) => l.trim()) : [],
    ...objectIdAndSearchType,
  };
};

const getPqlPayload = ({
  bookmark,
  page,
  query,
  activeSensors,
}: {
  bookmark: PqlFormValues;
  page: Page;
  query: string;
  activeSensors: number[];
}) => {
  return {
    name: bookmark.name.trim(),
    description: bookmark.description.trim() || '',
    labels:
      bookmark.labels && typeof bookmark.labels === 'string' ? bookmark.labels.split(',').map((l) => l.trim()) : [],
    searchType: (SEARCH_TYPE as Record<string, string>)[page],
    query: (bookmark.query ?? query).trim(),
    context: activeSensors,
    view: bookmark.view || '',
  };
};

const useCreateBookmark = () => {
  const api = useApi();
  const client = useQueryClient();

  return useMutation({
    mutationFn: async (params: CreateBookmarkParams) => {
      let createdBookmark;

      if (params.type === 'id') {
        const records = client.getQueryData<IdBookmark[]>(['bookmarks', 'id']);
        createdBookmark = await api.post(
          '/bookmarks/id',
          getIdPayload({ record: params.record, values: params.values }),
        );
        client.setQueryData(['bookmarks', 'id'], [...(records ?? []), createdBookmark as unknown as IdBookmark]);
      } else {
        const records = client.getQueryData<PqlBookmark[]>(['bookmarks', 'pql']);
        createdBookmark = await api.post(
          '/bookmarks/pql',
          getPqlPayload({
            bookmark: params.bookmark,
            page: params.page,
            query: params.query,
            activeSensors: params.activeSensors,
          }),
        );
        client.setQueryData(['bookmarks', 'pql'], [...(records ?? []), createdBookmark as unknown as PqlBookmark]);
      }
    },
  });
};

export { useCreateBookmark };
